import React from 'react';
import {
  Pill,
  Tag,
} from '../pages/dashboard/dashboard-units-components/dashboard-units.styled';
import {
  ShiftCard,
  CardHead,
  CardTitle,
  CardNote,
  Group,
  GroupTitle,
  PersonRow,
  PersonName,
  PersonMeta,
  Mono,
  EmptyLine,
  Caveat,
} from '../shifts/shifts.styled';
import { clockTime, elapsed } from '../../function-utils/shift-time';
import {
  IqOnDuty,
  OnDutyPerson,
  OnDutyProvenance,
} from '../../interfaces/iq-interfaces';

/**
 * WHO WAS ON DUTY — the block that sits beside the handover.
 *
 * ════════════════ IT IS DELIBERATELY NOT AN AI SURFACE ════════════════
 * It arrives on an IQ response only because that is where the shift window is
 * resolved. The content is computed in SQL (lobbero_be/src/iq/on-duty.js) and
 * injected server-side exactly like the incident floor; the model never sees a
 * staff identity and is structurally incapable of emitting one, because
 * serialize.buildEntryElement — "THE ONLY PLACE A VALUE IS INTERPOLATED INTO
 * THE PROMPT" — does not interpolate authorId (on-duty.js:32-38).
 *
 * So this card carries the plain product chrome, never `gradient.aurora` and
 * never the ✦ mark. Putting the AI badge on a list of named employees would
 * claim a model wrote it, and the reason that claim is dangerous is written
 * into the backend: a hallucinated name against a staff roster is worse than a
 * wrong count, because a wrong count is visibly a number and a wrong name is an
 * accusation.
 *
 * ════════════════ THE TWO KINDS OF PROVENANCE IT MUST SHOW ════════════════
 * PER PERSON: `clocked` is the person's own statement — a row they created in
 * `shifts`. `derived` is an inference from the rows they authored. The second
 * exists so a forgotten clock-in never produces a handover claiming nobody was
 * on duty while the log is full of that person's entries (on-duty.js:6-9), but
 * a derived span must NEVER be presented as though somebody clocked it.
 *
 * BLOCK LEVEL: 'clocked' | 'derived' | 'mixed' | 'none' answers "how much of
 * this list is a record and how much is an inference?" in one glance, which is
 * the question a manager actually has and which per-person tags alone answer
 * only after they have read every row.
 */

const COPY = {
  title: 'Who was on duty',

  // The block's own disclaimer. It exists because this card sits directly under
  // an aurora-marked AI summary, and proximity is enough for a reader to assume
  // the same authorship.
  notAi: 'Taken from the shift clock and the log — not written by Lobbero IQ.',

  provClocked: 'From the shift clock',
  provDerived: 'Inferred from log activity',
  provMixed: 'Part clock, part inferred',
  provNone: 'No record',

  srcClocked: 'clocked',
  srcDerived: 'inferred',

  stillOpen: 'still open',
  retro: 'closed later',

  // A derived span is an inference and says so in full, once, under the list.
  derivedCaveat:
    'An inferred span is the first and last thing that person recorded, not a clock record. Somebody who worked without recording anything will not appear here.',

  // The quiet-shift rule. Never soften this into a roster: who is ASSIGNED to a
  // building is not who worked it, and printing the roster would turn "we do not
  // know" into a false statement about a named person (on-duty.js:187-194).
  none: 'Nobody clocked in for this window and nothing was recorded in it.',
  noneCaveat:
    'Lobbero does not guess. It will not fall back to the building roster, because who is assigned to a building is not evidence of who worked the shift.',

  editOnlyTitle: 'Edited entries in this window',
  editOnlyCaveat:
    'These people corrected entries but recorded none of their own, so they are not placed on duty — an edit can be made from anywhere. They are listed because a correction session is still worth knowing about.',

  unknownName: 'Unnamed account',
};

const PROVENANCE_LABEL: Record<OnDutyProvenance, string> = {
  clocked: COPY.provClocked,
  derived: COPY.provDerived,
  mixed: COPY.provMixed,
  none: COPY.provNone,
};

// Tone tracks CERTAINTY, not sentiment. A fully clocked block is the strong
// case; anything containing an inference is warning-toned so the reader is told
// to look at the per-person tags before quoting a span at somebody.
const PROVENANCE_TONE: Record<
  OnDutyProvenance,
  'success' | 'warning' | 'neutral'
> = {
  clocked: 'success',
  derived: 'warning',
  mixed: 'warning',
  none: 'neutral',
};

const spanLabel = (p: OnDutyPerson, tz: string): string => {
  const from = clockTime(p.from, tz);
  // An open-ended shift has NO end, and the server refuses to invent one
  // (on-duty.js:123-126). Neither does this.
  if (p.still_open || !p.to) return `${from} → ${COPY.stillOpen}`;

  return `${from} – ${clockTime(p.to, tz)} · ${elapsed(p.from, p.to)}`;
};

const PersonLine: React.FC<{ person: OnDutyPerson; tz: string }> = ({
  person,
  tz,
}) => (
  <PersonRow>
    <PersonName>{person.name || COPY.unknownName}</PersonName>
    <Tag tone={person.source === 'clocked' ? 'success' : 'warning'}>
      {person.source === 'clocked' ? COPY.srcClocked : COPY.srcDerived}
    </Tag>
    <Mono>{spanLabel(person, tz)}</Mono>
    {person.ended_retroactively ? (
      <Tag tone="neutral">{COPY.retro}</Tag>
    ) : null}
    <PersonMeta>
      {`${person.entry_count} entries · ${person.edit_count} edits`}
    </PersonMeta>
  </PersonRow>
);

interface Props {
  data: IqOnDuty;
  tz: string;
}

export const IqOnDutyBlock: React.FC<Props> = ({ data, tz }) => {
  const people = data.people || [];
  const editOnly = data.edit_only || [];
  const hasDerived = people.some(p => p.source === 'derived');

  return (
    <ShiftCard>
      <CardHead>
        <CardTitle>{COPY.title}</CardTitle>
        <Pill tone={PROVENANCE_TONE[data.provenance]}>
          {PROVENANCE_LABEL[data.provenance]}
        </Pill>
      </CardHead>
      <CardNote>{COPY.notAi}</CardNote>

      {people.length ? (
        <Group>
          {people.map(p => (
            <PersonLine key={p.user_id} person={p} tz={tz} />
          ))}
        </Group>
      ) : (
        <>
          <EmptyLine>{COPY.none}</EmptyLine>
          <Caveat>{COPY.noneCaveat}</Caveat>
        </>
      )}

      {hasDerived ? <Caveat>{COPY.derivedCaveat}</Caveat> : null}

      {editOnly.length ? (
        <Group>
          <GroupTitle>{COPY.editOnlyTitle}</GroupTitle>
          {editOnly.map(e => (
            <PersonRow key={e.user_id}>
              <PersonName>{e.name || COPY.unknownName}</PersonName>
              <PersonMeta>{`${e.edit_count} edits`}</PersonMeta>
            </PersonRow>
          ))}
          <Caveat>{COPY.editOnlyCaveat}</Caveat>
        </Group>
      ) : null}
    </ShiftCard>
  );
};
